import * as React from 'react'

import { requestPost, requestPut } from '../../../modules/request/index'
import {
  ADMIN,
  defaultSnackbarTimeout,
  getPageTitle,
  Snackbar,
  US_COLLEGE_SCHOOL_TYPE,
  US_HIGHER_ED_SCHOOL_TYPE,
} from '../../Shared/index'
import useSnackbarMonitor from '../../Shared/hooks/useSnackbarMonitor';
import TeacherDangerZone from '../components/accounts/edit/teacher_danger_zone'
import TeacherEmailNotifications from '../components/accounts/edit/teacher_email_notifications'
import TeacherGeneralAccountInfo from '../components/accounts/edit/teacher_general'
import TeacherGradeLevels from '../components/accounts/edit/teacher_grade_levels'
import TeacherLinkedAccounts from '../components/accounts/edit/teacher_linked_accounts'
import TeacherPasswordAccountInfo from '../components/accounts/edit/update_password'
import AccessibilitySettings from '../components/accounts/edit/accessibility_settings'
import TeacherSubjectAreas from '../components/accounts/edit/teacher_subject_areas'
import CheckboxSettingsSection from '../components/accounts/edit/checkbox_settings_section'
import { STUDENT_EXPERIENCE_SECTION_ID } from '../components/accounts/edit/section_ids'

type AlternativeSchool = unknown

interface AccountInfo {
  clever_id: string | null
  classlink_id?: string | null
  nycps_id?: string | null
  email: string
  google_id: string | null
  id: number | string
  maximum_grade_level: number
  minimum_grade_level: number
  name: string
  notification_email_frequency: string
  school: string
  school_type: string
  send_newsletter: boolean
  show_students_exact_score: boolean
  read_aloud_on_for_owned_classes_by_default: boolean
  read_aloud_available: boolean
  subject_area_ids: number[]
  uses_manage_activities_v2: boolean
  teacher_notification_settings: TeacherNotificationSettings
  time_zone: string
  role: string
}

type SubjectArea = {
  id: number
  name: string
}

type TeacherNotificationSettings = Record<string, unknown>

interface TeacherAccountProps {
  accountInfo: AccountInfo
  alternativeSchools: AlternativeSchool[]
  alternativeSchoolsNameMap: Record<string, string>
  classlinkId: string
  cleverLink: string
  provider: string | null
  providerJustSet: boolean
  postGoogleClassroomAssignments: boolean
  showDismissSchoolSelectionReminderCheckbox: boolean
  subjectAreas: SubjectArea[]
  collegeSubjectAreas: SubjectArea[]
}

function gradeLevelToOption(
  gradeLevel: number | null
): { label: string; value: number } | null {
  return gradeLevel != null
    ? { label: String(gradeLevel), value: gradeLevel }
    : null
}

const EMAIL_NOTIFICATIONS = 'emailNotifications'
const GENERAL = 'general'
const GRADE_LEVEL = 'gradeLevel'
const PASSWORD = 'password'
const SUBJECT_AREAS = 'subjectAreas'
const STUDENT_DASHBOARD_SETTINGS = 'studentDashboardSettings'
const ACCESSIBILITY_SETTINGS = 'accessibilitySettings'
const BETA_FEATURES = 'betaFeatures'

const STUDENT_DASHBOARD_GROUP = {
  attribute: 'show_students_exact_score',
  checkboxLabel: 'Show exact score for graded activities',
  description: <React.Fragment>Control what your classes see on their dashboard. <a className="focus-on-light" href="https://support.quill.org/en/articles/9034576-how-do-students-see-their-scores-and-time-spent" rel="noopener noreferrer" target="_blank">Learn more.</a></React.Fragment>,
}
const READ_ALOUD_GROUP = {
  attribute: 'read_aloud_on_for_owned_classes_by_default',
  checkboxId: 'read-aloud-on-for-owned-classes',
  checkboxLabel: 'Turn on read aloud for all students',
  description: 'Let students listen to activity prompts and passages read out loud.',
  heading: 'Read Aloud',
  note: 'You can turn read aloud on or off for individual students in Manage Classes.',
}
const BETA_FEATURES_GROUP = {
  attribute: 'uses_manage_activities_v2',
  checkboxLabel: 'New Manage Activities page',
  description: 'Try new Quill features early. You can switch back at any time.',
}

const TeacherAccount: FC<TeacherAccountProps> = ({
  accountInfo,
  alternativeSchools,
  alternativeSchoolsNameMap,
  cleverLink,
  provider,
  providerJustSet,
  postGoogleClassroomAssignments,
  showDismissSchoolSelectionReminderCheckbox,
  subjectAreas,
  collegeSubjectAreas,
}) => {
  const {
    clever_id: initialCleverId,
    classlink_id: initialClasslinkId,
    nycps_id: initialNycpsId,
    email: initialEmail,
    google_id: initialGoogleId,
    id,
    maximum_grade_level: initialMaximumGradeLevel,
    minimum_grade_level: initialMinimumGradeLevel,
    name: initialName,
    notification_email_frequency: initialNotificationEmailFrequency,
    school: initialSchool,
    school_type: initialSchoolType,
    send_newsletter: initialSendNewsletter,
    show_students_exact_score: initialShowStudentsExactScore,
    read_aloud_on_for_owned_classes_by_default: initialReadAloudOnByDefault,
    read_aloud_available: readAloudAvailable,
    uses_manage_activities_v2: initialUsesManageActivitiesV2,
    snackbar_duration: initialSnackbarDuration,
    subject_area_ids: initialSubjectAreaIds,
    teacher_notification_settings: initialTeacherNotificationSettings,
    time_zone: initialTimeZone,
  } = accountInfo

  const [activeSection, setActiveSection] = React.useState<string | null>(null)
  const [classlinkId, setClasslinkId] = React.useState<string | null>(initialClasslinkId)
  const [nycpsId, setNycpsId] = React.useState<string | null>(initialNycpsId)
  const [cleverId, setCleverId] = React.useState<string | null>(initialCleverId)
  const [email, setEmail] = React.useState(initialEmail)
  const [errors, setErrors] = React.useState<Record<string, unknown>>({})
  const [googleId, setGoogleId] = React.useState<string | null>(initialGoogleId)
  const [maximumGradeLevel, setMaximumGradeLevel] = React.useState<number>(initialMaximumGradeLevel)
  const [minimumGradeLevel, setMinimumGradeLevel] = React.useState<number>(initialMinimumGradeLevel)
  const [name, setName] = React.useState(initialName)
  const [notificationEmailFrequency, setNotificationEmailFrequency] = React.useState(initialNotificationEmailFrequency)
  const [postNotificationEmailFrequency, setPostNotificationEmailFrequency] = React.useState(initialNotificationEmailFrequency)
  const [school, setSchool] = React.useState(initialSchool)
  const [schoolType, setSchoolType] = React.useState(initialSchoolType)
  const [sendNewsletter, setSendNewsletter] = React.useState(initialSendNewsletter)
  const [postSendNewsletter, setPostSendNewsletter] = React.useState(initialSendNewsletter)
  const [selectedSubjectAreaIds, setSelectedSubjectAreaIds] = React.useState<number[]>(initialSubjectAreaIds)
  const [showStudentsExactScore, setShowStudentsExactScore] = React.useState(initialShowStudentsExactScore)
  const [readAloudOnByDefault, setReadAloudOnByDefault] = React.useState(initialReadAloudOnByDefault)
  const [usesManageActivitiesV2, setUsesManageActivitiesV2] = React.useState(initialUsesManageActivitiesV2)
  const [snackbarDuration, setSnackbarDuration] = React.useState(initialSnackbarDuration || defaultSnackbarTimeout)
  const [snackbarCopy, setSnackbarCopy] = React.useState('')
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [timesSubmitted, setTimesSubmitted] = React.useState(0)
  const [teacherNotificationSettings, setTeacherNotificationSettings] = React.useState<TeacherNotificationSettings>(initialTeacherNotificationSettings)
  const [tempTeacherNotificationSettings, setTempTeacherNotificationSettings,] = React.useState<TeacherNotificationSettings>(initialTeacherNotificationSettings)
  const [timeZone, setTimeZone] = React.useState(initialTimeZone)

  const isAdmin = accountInfo.role === ADMIN
  const isCollege = [US_COLLEGE_SCHOOL_TYPE, US_HIGHER_ED_SCHOOL_TYPE].includes(schoolType)
  const activeSubjectAreas = isCollege ? collegeSubjectAreas : subjectAreas
  const activeSelectedSubjectAreaIds = React.useMemo(
    () => selectedSubjectAreaIds.filter(id => activeSubjectAreas.some(subjectArea => subjectArea.id === id)),
    [selectedSubjectAreaIds, activeSubjectAreas]
  )

  useSnackbarMonitor(showSnackbar, setShowSnackbar, snackbarDuration, true);

  React.useEffect(() => {
    document.title = getPageTitle('Home', 'My Account')
  }, [])

  React.useEffect(() => {
    document.getElementById(window.location.hash.slice(1))?.scrollIntoView()
  }, [])

  React.useEffect(() => {
    if (!providerJustSet) { return }

    const copy = `${providerJustSet} linked`
    setSnackbarCopy(copy)
    showTransientSnackbar()
  }, [])

  function activateSection(section: string) {
    setActiveSection(section)
  }

  function deactivateSection(section: string) {
    if (activeSection === section) {
      setActiveSection(null)
      setErrors({})
    }
  }

  function sectionProps(section: string) {
    return {
      activateSection: () => activateSection(section),
      active: activeSection === section,
      deactivateSection: () => deactivateSection(section),
    }
  }

  function clearClasslinkId() {
    setClasslinkId(null)
  }

  function deleteAccount() {
    requestPost(
      `${window.postbuildEnv.DEFAULT_URL}/teachers/clear_data/${id}`,
      {},
      () => {
        window.location.href = window.location.origin
      }
    )
  }

  function resetTeacherNotificationSection() {
    setPostNotificationEmailFrequency(notificationEmailFrequency)
    setPostSendNewsletter(sendNewsletter)
    setTempTeacherNotificationSettings(teacherNotificationSettings)
  }

  function showTransientSnackbar() {
    setShowSnackbar(true)
  }

  function updateNotificationSettings(data: unknown) {
    requestPost(
      '/api/v1/teacher_notification_settings/bulk_update',
      data,
      (body: { teacher_notification_settings: TeacherNotificationSettings }) => {
        setTeacherNotificationSettings(body.teacher_notification_settings)
        setTempTeacherNotificationSettings(body.teacher_notification_settings)
        setErrors({})
        setActiveSection(null)
      },
      (body: { errors: Record<string, unknown> }) => {
        setErrors(body.errors)
        setTimesSubmitted((t) => t + 1)
      }
    )
  }

  function updateTeacherInfo(
    data: unknown,
    copy: string
  ) {
    requestPut(
      '/teacher_infos',
      data,
      (body: {
        maximum_grade_level: number
        minimum_grade_level: number
        notification_email_frequency: string
        show_students_exact_score: boolean
        read_aloud_on_for_owned_classes_by_default: boolean
        subject_area_ids: number[]
        uses_manage_activities_v2: boolean
      }) => {
        setErrors({})
        setMaximumGradeLevel(body.maximum_grade_level)
        setMinimumGradeLevel(body.minimum_grade_level)
        setNotificationEmailFrequency(body.notification_email_frequency)
        setPostNotificationEmailFrequency(body.notification_email_frequency)
        setSelectedSubjectAreaIds(body.subject_area_ids)
        setShowStudentsExactScore(body.show_students_exact_score)
        setReadAloudOnByDefault(body.read_aloud_on_for_owned_classes_by_default)
        setUsesManageActivitiesV2(body.uses_manage_activities_v2)

        setSnackbarCopy(copy)
        if (copy) showTransientSnackbar()
        setActiveSection(null)
      },
      (body: { errors: Record<string, unknown> }) => {
        setErrors(body.errors || {})
        setTimesSubmitted((t) => t + 1)
      }
    )
  }

  function updateAccessibilitySettings(
    data: unknown,
    copy: string
  ) {
    requestPut(
      '/accessibility_setting',
      data,
      (body: {
        snackbar_duration: number
      }) => {
        setErrors({})
        setSnackbarDuration(body.snackbar_duration)

        setSnackbarCopy(copy)
        if (copy) showTransientSnackbar()
        setActiveSection(null)
      },
      (body: { errors: Record<string, unknown> }) => {
        setErrors(body.errors)
        setTimesSubmitted((t) => t + 1)
      }
    )
  }

  function updateUser(
    data: unknown,
    url: string,
    copy: string,
    onSuccess: () => void|null
  ) {
    requestPut(
      `${window.postbuildEnv.DEFAULT_URL}${url}`,
      data,
      (body: {
        clever_id: string | null
        classlink_id: string | null
        nycps_id: string | null
        email: string
        google_id: string | null
        name: string
        school: string
        school_type: string
        send_newsletter: boolean,
        time_zone: string
      }) => {
        setErrors({})
        setClasslinkId(body.classlink_id)
        setNycpsId(body.nycps_id)
        setCleverId(body.clever_id)
        setEmail(body.email)
        setGoogleId(body.google_id)
        setName(body.name)
        setSchool(body.school)
        setSchoolType(body.school_type)
        setPostSendNewsletter(body.send_newsletter)
        setSendNewsletter(body.send_newsletter)
        setTimeZone(body.time_zone)

        setSnackbarCopy(copy)
        showTransientSnackbar()
        setActiveSection(null)

        if (onSuccess) onSuccess()
      },
      (body: { errors: Record<string, unknown> }) => {
        setErrors(body.errors)
        setTimesSubmitted((t) => t + 1)
      }
    )
  }

  function handleChangeTeacherNotificationOption(key, value) {
    if (key === 'postNotificationEmailFrequency') {
      setPostNotificationEmailFrequency(value as string)
    }

    if (key === 'postSendNewsletter') {
      setPostSendNewsletter(value as boolean)
    }

    if (key === 'tempTeacherNotificationSettings') {
      setTempTeacherNotificationSettings(value as TeacherNotificationSettings)
    }
  }

  return (
    <main
      className="user-account white-background-accommodate-footer"
      id="post-secondary-nav"
    >
      <TeacherGeneralAccountInfo
        {...sectionProps(GENERAL)}
        alternativeSchools={alternativeSchools}
        alternativeSchoolsNameMap={alternativeSchoolsNameMap}
        classlinkId={classlinkId}
        cleverId={cleverId}
        email={email}
        errors={errors}
        googleId={googleId}
        name={name}
        provider={provider}
        school={school}
        schoolType={schoolType}
        showDismissSchoolSelectionReminderCheckbox={showDismissSchoolSelectionReminderCheckbox}
        timesSubmitted={timesSubmitted}
        timeZone={timeZone}
        updateUser={updateUser}
      />

      <TeacherPasswordAccountInfo
        {...sectionProps(PASSWORD)}
        cleverId={cleverId}
        errors={errors}
        googleId={googleId}
        role={accountInfo.role}
        timesSubmitted={timesSubmitted}
        updateUser={updateUser}
      />

      <TeacherLinkedAccounts
        classlinkId={classlinkId}
        cleverId={cleverId}
        cleverLink={cleverLink}
        email={email}
        errors={errors}
        googleId={googleId}
        nycpsId={nycpsId}
        onUserUpdateSuccess={clearClasslinkId}
        postGoogleClassroomAssignments={postGoogleClassroomAssignments}
        timesSubmitted={timesSubmitted}
        updateUser={updateUser}

        user={accountInfo}
      />

      <TeacherEmailNotifications
        {...sectionProps(EMAIL_NOTIFICATIONS)}
        notificationEmailFrequency={postNotificationEmailFrequency}
        notificationSettings={tempTeacherNotificationSettings}
        resetTeacherNotificationSection={resetTeacherNotificationSection}
        sendNewsletter={postSendNewsletter}
        setTeacherNotificationOption={handleChangeTeacherNotificationOption}
        updateNotificationSettings={updateNotificationSettings}
        updateTeacherInfo={updateTeacherInfo}
        updateUser={updateUser}
      />

      {!isAdmin && !isCollege && (
        <TeacherGradeLevels
          {...sectionProps(GRADE_LEVEL)}
          passedMaximumGradeLevel={gradeLevelToOption(maximumGradeLevel)}
          passedMinimumGradeLevel={gradeLevelToOption(minimumGradeLevel)}
          updateTeacherInfo={updateTeacherInfo}
        />
      )}

      {!isAdmin && (
        <TeacherSubjectAreas
          {...sectionProps(SUBJECT_AREAS)}
          passedSelectedSubjectAreaIds={activeSelectedSubjectAreaIds}
          subjectAreas={activeSubjectAreas}
          updateTeacherInfo={updateTeacherInfo}
        />
      )}

      <CheckboxSettingsSection
        {...sectionProps(STUDENT_DASHBOARD_SETTINGS)}
        groups={readAloudAvailable ? [STUDENT_DASHBOARD_GROUP, READ_ALOUD_GROUP] : [STUDENT_DASHBOARD_GROUP]}
        passedValues={{ show_students_exact_score: showStudentsExactScore, read_aloud_on_for_owned_classes_by_default: readAloudOnByDefault, }}
        sectionClassName="student-dashboard-settings-section"
        sectionId={STUDENT_EXPERIENCE_SECTION_ID}
        title="Student Experience"
        updateTeacherInfo={updateTeacherInfo}
      />

      <AccessibilitySettings
        {...sectionProps(ACCESSIBILITY_SETTINGS)}
        passedSnackbarDuration={snackbarDuration}
        updateAccessibilitySettings={updateAccessibilitySettings}
      />

      <CheckboxSettingsSection
        {...sectionProps(BETA_FEATURES)}
        groups={[BETA_FEATURES_GROUP]}
        passedValues={{ uses_manage_activities_v2: usesManageActivitiesV2, }}
        sectionClassName="beta-features-settings-section"
        sectionId="betaFeaturesTitle"
        title="Beta Features"
        updateTeacherInfo={updateTeacherInfo}
      />

      <TeacherDangerZone deleteAccount={deleteAccount} />

      <Snackbar text={snackbarCopy} visible={showSnackbar} />
    </main>
  )
}

export default TeacherAccount
